import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, query, where, orderBy, getDocs, doc, deleteDoc, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../firebase';

interface Task {
  id?: string;
  title: string;
  category: 'Scales' | 'Exercises' | 'Studies' | 'Repertoire' | 'Ensemble';
  isCustom: boolean;
  createdBy?: 'student' | 'teacher';
  teacherId?: string;
  practiceReminders?: string[];
}

interface QuizQuestion {
  question: string;
  answer: string;
}

interface Lesson {
  id: string;
  lessonId: string;
  studentId: string;
  teacherId?: string;
  createdAt: any;
  summaryTxt?: string; // Legacy field
  overview?: string;
  practiceReminders?: string | Array<{
    type: 'section' | 'subsection' | 'bullet' | 'text';
    content: string;
    level: number;
  }>; // Support both old string and new structured array format
  mindsetMessages?: string;
  keyQuotes?: string[];
  tasks: Task[];
  quiz: QuizQuestion[];
  transcript?: string;
  status: string;
  processedAt: any;
}

const LessonSummaries: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { studentId } = useParams<{ studentId?: string }>();
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedLessons, setExpandedLessons] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [deletingLesson, setDeletingLesson] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [customTasks, setCustomTasks] = useState<Task[]>([]);
  const [transferringReminder, setTransferringReminder] = useState<string | null>(null);
  const [transferredReminders, setTransferredReminders] = useState<Set<string>>(new Set());
  const [creatingTaskFromReminder, setCreatingTaskFromReminder] = useState<string | null>(null);
  const [newTaskCategory, setNewTaskCategory] = useState<'Scales' | 'Exercises' | 'Studies' | 'Repertoire' | 'Ensemble'>('Scales');
  const [transferredRemindersLoaded, setTransferredRemindersLoaded] = useState(false);

  // Determine if this is a teacher viewing a student's lessons
  const isTeacherView = studentId && currentUser?.role === 'teacher';
  const targetStudentId = isTeacherView ? studentId : currentUser?.uid;

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (!targetStudentId) {
      setError('Unable to load lessons');
      setLoading(false);
      return;
    }

    fetchLessons();
    loadCustomTasks();
    loadTransferredReminders();
  }, [currentUser, targetStudentId, navigate]);

  const fetchLessons = async () => {
    try {
      setLoading(true);
      
      // First try with the composite index (orderBy included)
      let lessonsQuery = query(
        collection(db, 'lessons'),
        where('studentId', '==', targetStudentId),
        orderBy('createdAt', 'desc')
      );

      try {
        const querySnapshot = await getDocs(lessonsQuery);
        const lessonsData: Lesson[] = [];

        querySnapshot.forEach((doc) => {
          lessonsData.push({
            id: doc.id,
            ...doc.data()
          } as Lesson);
        });

        setLessons(lessonsData);
      } catch (indexError: any) {
        // If index not ready, fallback to simple query and sort client-side
        if (indexError.code === 'failed-precondition') {
          console.log('Index not ready, using fallback query...');
          
          const fallbackQuery = query(
            collection(db, 'lessons'),
            where('studentId', '==', targetStudentId)
          );

          const querySnapshot = await getDocs(fallbackQuery);
          const lessonsData: Lesson[] = [];

          querySnapshot.forEach((doc) => {
            lessonsData.push({
              id: doc.id,
              ...doc.data()
            } as Lesson);
          });

          // Sort by createdAt descending on client side
          lessonsData.sort((a, b) => {
            const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return bTime - aTime;
          });

          setLessons(lessonsData);
        } else {
          throw indexError;
        }
      }
    } catch (err) {
      console.error('Error fetching lessons:', err);
      setError('Failed to load lessons');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (lessonId: string) => {
    const newExpanded = new Set(expandedLessons);
    if (newExpanded.has(lessonId)) {
      newExpanded.delete(lessonId);
    } else {
      newExpanded.add(lessonId);
    }
    setExpandedLessons(newExpanded);
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'Unknown date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPreviewText = (text: string, maxLength: number = 80): string => {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const handleDeleteLesson = async (lessonId: string) => {
    try {
      setDeletingLesson(lessonId);
      setShowDeleteConfirm(null);
      
      await deleteDoc(doc(db, 'lessons', lessonId));
      
      // Remove the lesson from the local state
      setLessons(prevLessons => prevLessons.filter(lesson => lesson.id !== lessonId));
      
      // Remove from expanded lessons if it was expanded
      setExpandedLessons(prev => {
        const newSet = new Set(prev);
        newSet.delete(lessonId);
        return newSet;
      });
      
    } catch (err) {
      console.error('Error deleting lesson:', err);
      setError('Failed to delete lesson');
    } finally {
      setDeletingLesson(null);
    }
  };

  const confirmDelete = (lessonId: string) => {
    setShowDeleteConfirm(lessonId);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(null);
  };

  const loadCustomTasks = async () => {
    try {
      if (!targetStudentId) return;
      
      const tasksQuery = query(
        collection(db, 'customTasks'),
        where('studentId', '==', targetStudentId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(tasksQuery);
      const tasksData: Task[] = [];
      
      querySnapshot.forEach((doc) => {
        tasksData.push({
          id: doc.id,
          ...doc.data()
        } as Task);
      });
      
      setCustomTasks(tasksData);
    } catch (err) {
      console.error('Error loading custom tasks:', err);
    }
  };

  const transferReminderToTask = async (reminderText: string, taskId: string) => {
    try {
      setTransferringReminder(reminderText);
      
      const taskRef = doc(db, 'customTasks', taskId);
      const currentTask = customTasks.find(t => t.id === taskId);
      if (!currentTask) return;

      const updatedReminders = [...(currentTask.practiceReminders || []), reminderText.trim()];
      
      await updateDoc(taskRef, {
        practiceReminders: updatedReminders
      });

      // Update local state
      setCustomTasks(prev => prev.map(t => 
        t.id === taskId ? { ...t, practiceReminders: updatedReminders } : t
      ));

      // Mark reminder as transferred and save to Firestore
      await markReminderAsTransferred(reminderText.trim());

      // Show success message (you could add a toast notification here)
      console.log('Reminder added to task successfully');
    } catch (error) {
      console.error('Error transferring reminder:', error);
      setError('Failed to add reminder to task');
    } finally {
      setTransferringReminder(null);
    }
  };

  const createTaskFromReminder = async (reminderText: string) => {
    try {
      setCreatingTaskFromReminder(reminderText);
      
      if (!targetStudentId) return;

      const taskData: any = {
        studentId: targetStudentId,
        title: reminderText.trim(),
        category: newTaskCategory,
        isCustom: true,
        createdBy: currentUser?.role === 'teacher' ? 'teacher' : 'student',
        practiceReminders: [],
        createdAt: new Date()
      };

      // Only add teacherId if the current user is a teacher
      if (currentUser?.role === 'teacher') {
        taskData.teacherId = currentUser.uid;
      }
      
      const docRef = await addDoc(collection(db, 'customTasks'), taskData);
      
      const newTask: Task = {
        id: docRef.id,
        title: reminderText.trim(),
        category: newTaskCategory,
        isCustom: true,
        createdBy: currentUser?.role === 'teacher' ? 'teacher' : 'student',
        practiceReminders: []
      };

      // Only add teacherId if the current user is a teacher
      if (currentUser?.role === 'teacher') {
        (newTask as any).teacherId = currentUser.uid;
      }

      // Update local state
      setCustomTasks(prev => [newTask, ...prev]);

      // Mark reminder as transferred and save to Firestore
      await markReminderAsTransferred(reminderText.trim());

      console.log('Task created from reminder successfully');
    } catch (error) {
      console.error('Error creating task from reminder:', error);
      setError('Failed to create task from reminder');
    } finally {
      setCreatingTaskFromReminder(null);
    }
  };

  const markReminderAsTransferred = async (reminderText: string) => {
    try {
      if (!targetStudentId) return;

      // Save to Firestore
      await addDoc(collection(db, 'transferredReminders'), {
        studentId: targetStudentId,
        reminderText: reminderText.trim(),
        transferredAt: new Date(),
        transferredBy: currentUser?.uid
      });

      // Update local state
      setTransferredReminders(prev => new Set(Array.from(prev).concat(reminderText.trim())));
    } catch (error) {
      console.error('Error marking reminder as transferred:', error);
    }
  };

  const loadTransferredReminders = async () => {
    try {
      if (!targetStudentId) return;

      const querySnapshot = await getDocs(
        query(
          collection(db, 'transferredReminders'),
          where('studentId', '==', targetStudentId)
        )
      );

      const transferredSet = new Set<string>();
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.reminderText) {
          transferredSet.add(data.reminderText);
        }
      });

      setTransferredReminders(transferredSet);
      setTransferredRemindersLoaded(true);
    } catch (error) {
      console.error('Error loading transferred reminders:', error);
      setTransferredRemindersLoaded(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mb-4"></div>
          <p className="text-gray-600">Loading lesson summaries...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <nav className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="flex items-center">
                <div className="text-3xl mr-3">📚</div>
                <h1 className="text-sm sm:text-xl font-bold text-white">
                  Lesson Summaries
                </h1>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 backdrop-blur-sm"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          
          {/* Exciting Hero Section for Students */}
          {!isTeacherView && (
            <div className="relative mb-8 overflow-hidden rounded-3xl">
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 opacity-90"></div>
              <div className="absolute inset-0 bg-black opacity-10"></div>
              <div className="relative px-8 py-12 text-center">
                <div className="hidden sm:flex justify-center mb-6">
                  <div className="relative">
                    <div className="text-8xl animate-bounce">🎵</div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-300 rounded-full animate-ping"></div>
                    <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-orange-300 rounded-full animate-pulse"></div>
                  </div>
                </div>
                <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-white mb-2 sm:mb-4">
                  Your Amazing Lesson Journey! 🌟
                </h2>
                <p className="text-sm sm:text-xl text-orange-100 max-w-2xl mx-auto mb-4 sm:mb-6">
                  Discover all the incredible progress you've made and the wonderful things you've learned!
                </p>
                
                {/* Achievement Stats */}
                <div className="flex justify-center space-x-4 sm:space-x-8 mb-4 sm:mb-6">
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl px-3 sm:px-6 py-2 sm:py-4 text-white">
                    <div className="text-xl sm:text-3xl font-bold">{lessons.length}</div>
                    <div className="text-xs sm:text-sm font-medium">Lessons Completed</div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl px-3 sm:px-6 py-2 sm:py-4 text-white">
                    <div className="text-xl sm:text-3xl font-bold">🎯</div>
                    <div className="text-xs sm:text-sm font-medium">Keep Going!</div>
                  </div>
                  <div className="bg-white/20 backdrop-blur-sm rounded-xl sm:rounded-2xl px-3 sm:px-6 py-2 sm:py-4 text-white">
                    <div className="text-xl sm:text-3xl font-bold">⭐</div>
                    <div className="text-xs sm:text-sm font-medium">You're Awesome!</div>
                  </div>
                </div>
                
                {lessons.length > 0 && (
                  <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 inline-block">
                    <p className="text-orange-100 text-lg font-medium">
                      🎉 Wow! You've completed {lessons.length} amazing lesson{lessons.length !== 1 ? 's' : ''}! 🎉
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Teacher View Header */}
          {isTeacherView && (
            <div className="mb-6">
              <h2 className="text-lg sm:text-2xl font-bold text-gray-900 mb-2">
                Student Lesson Summaries
              </h2>
              <p className="text-gray-600">
                {lessons.length} lesson{lessons.length !== 1 ? 's' : ''} recorded
              </p>
            </div>
          )}

          {lessons.length === 0 ? (
            <div className="relative bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 rounded-3xl p-12 text-center shadow-2xl border border-purple-200 overflow-hidden">
              {/* Decorative background elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-200 to-purple-200 rounded-full -translate-y-16 translate-x-16 opacity-30"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-pink-200 to-rose-200 rounded-full translate-y-12 -translate-x-12 opacity-30"></div>
              
              <div className="relative">
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="text-8xl animate-pulse">🎵</div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-blue-400 rounded-full animate-ping"></div>
                    <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-purple-400 rounded-full animate-pulse"></div>
                  </div>
                </div>
                
                <h3 className="text-xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2 sm:mb-4">
                  Ready for Your First Adventure? 🚀
                </h3>
                
                <p className="text-sm sm:text-lg text-gray-700 mb-4 sm:mb-8 max-w-md mx-auto">
                  {isTeacherView 
                    ? 'This student hasn\'t recorded any lessons yet.' 
                    : 'Your musical journey is about to begin! Record your first lesson and watch the magic happen! ✨'
                  }
                </p>
                
                {!isTeacherView && (
                  <div className="space-y-4">
                    <button
                      onClick={() => navigate('/record-lesson')}
                      className="group relative bg-gradient-to-r from-blue-500 via-purple-600 to-pink-600 hover:from-blue-600 hover:to-pink-700 text-white px-6 sm:px-10 py-3 sm:py-5 rounded-xl sm:rounded-2xl font-bold text-base sm:text-xl shadow-2xl transform hover:scale-110 transition-all duration-300 overflow-hidden"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="relative flex items-center justify-center">
                        <div className="text-2xl mr-3 group-hover:animate-bounce">🎙️</div>
                        <span>Start Your First Lesson!</span>
                      </div>
                    </button>
                    
                    <div className="flex justify-center space-x-8 mt-6">
                      <div className="bg-white/60 backdrop-blur-sm rounded-xl px-4 py-2">
                        <div className="text-2xl mb-1">🎯</div>
                        <div className="text-sm font-medium text-gray-700">Track Progress</div>
                      </div>
                      <div className="bg-white/60 backdrop-blur-sm rounded-xl px-4 py-2">
                        <div className="text-2xl mb-1">📝</div>
                        <div className="text-sm font-medium text-gray-700">Get Notes</div>
                      </div>
                      <div className="bg-white/60 backdrop-blur-sm rounded-xl px-4 py-2">
                        <div className="text-2xl mb-1">🏆</div>
                        <div className="text-sm font-medium text-gray-700">Earn Badges</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {lessons.map((lesson, index) => (
                <div key={lesson.id} className={`relative bg-gradient-to-br from-white via-blue-50 to-purple-50 shadow-xl rounded-2xl overflow-hidden border border-purple-100 transform hover:scale-[1.02] transition-all duration-300 ${expandedLessons.has(lesson.id) ? 'ring-2 ring-purple-300' : ''}`}>
                  {/* Achievement Badge */}
                  <div className="absolute top-4 right-4">
                    <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg">
                      <span className="text-xl font-bold">{index + 1}</span>
                    </div>
                  </div>
                  
                  <div className="px-6 py-6">
                    {/* Desktop Layout */}
                    <div className="hidden sm:flex justify-between items-start">
                      <div 
                        className="flex-1 cursor-pointer hover:bg-white/50 transition-colors rounded-lg p-3 -m-3"
                        onClick={() => toggleExpanded(lesson.id)}
                      >
                        <div className="flex items-center mb-2 sm:mb-3">
                          <div className="text-2xl sm:text-3xl mr-2 sm:mr-3 animate-pulse">📚</div>
                          <div>
                            <h3 className="text-base sm:text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1">
                              Lesson #{index + 1} - {formatDate(lesson.createdAt)}
                            </h3>
                            <div className="flex items-center space-x-2">
                              <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                                ✅ Completed
                              </span>
                              <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                                📖 Notes Ready
                              </span>
                            </div>
                          </div>
                        </div>
                        <p className="text-gray-700 text-base leading-relaxed">
                          {getPreviewText(lesson.overview || lesson.summaryTxt || 'No summary available')}
                        </p>
                      </div>
                      <div className="ml-4 flex-shrink-0 flex items-center space-x-2">
                        {!isTeacherView && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDelete(lesson.id);
                            }}
                            disabled={deletingLesson === lesson.id}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded-md transition-colors disabled:opacity-50"
                            title="Delete lesson"
                          >
                            {deletingLesson === lesson.id ? (
                              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => toggleExpanded(lesson.id)}
                          className="group bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-4 py-2 rounded-xl font-medium text-sm shadow-lg transform hover:scale-105 transition-all duration-200"
                        >
                          <div className="flex items-center">
                            <span className="mr-2">
                              {expandedLessons.has(lesson.id) ? '📖 Hide Details' : '👀 View Details'}
                            </span>
                            <svg
                              className={`h-4 w-4 transform transition-transform ${
                                expandedLessons.has(lesson.id) ? 'rotate-180' : ''
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Mobile Layout - 3 Lines */}
                    <div className="sm:hidden">
                      {/* Line 1: Lesson No, date/time, and View Details button */}
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="text-base font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                          Lesson #{index + 1} - {formatDate(lesson.createdAt)}
                        </h3>
                        <button
                          onClick={() => toggleExpanded(lesson.id)}
                          className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-3 py-1.5 rounded-lg font-medium text-xs shadow-lg"
                        >
                          {expandedLessons.has(lesson.id) ? '📖 Hide' : '👀 View Details'}
                        </button>
                      </div>

                      {/* Line 2: Books icon, completed check, Notes ready, and delete icon */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                          <div className="text-lg animate-pulse">📚</div>
                          <span className="bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                            ✅ Completed
                          </span>
                          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full">
                            📖 Notes Ready
                          </span>
                        </div>
                        {!isTeacherView && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDelete(lesson.id);
                            }}
                            disabled={deletingLesson === lesson.id}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded-md transition-colors disabled:opacity-50"
                            title="Delete lesson"
                          >
                            {deletingLesson === lesson.id ? (
                              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                          </button>
                        )}
                      </div>

                      {/* Line 3: Beginning of lesson notes */}
                      <div 
                        className="cursor-pointer hover:bg-white/50 transition-colors rounded-lg p-2 -m-2"
                        onClick={() => toggleExpanded(lesson.id)}
                      >
                        <p className="text-gray-700 text-sm leading-relaxed">
                          {getPreviewText(lesson.overview || lesson.summaryTxt || 'No summary available')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {expandedLessons.has(lesson.id) && (
                    <div className="px-6 py-6 border-t border-purple-200 bg-gradient-to-br from-blue-50 to-purple-50">
                      {/* Motivational Message */}
                      {!isTeacherView && (
                        <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-xl p-4 mb-6 border border-green-200">
                          <div className="flex items-center">
                            <div className="text-2xl mr-3">🎉</div>
                            <div>
                              <p className="text-green-800 font-medium text-sm">
                                Amazing work! You've completed another lesson! 🌟
                              </p>
                              <p className="text-green-700 text-xs mt-1">
                                Keep up the fantastic progress - you're becoming an incredible musician!
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      <div className="space-y-6">
                        {/* Lesson Overview */}
                        <div className="bg-white rounded-xl p-6 shadow-lg border border-blue-100">
                          <div className="flex items-center mb-4">
                            <div className="text-2xl mr-3">📝</div>
                            <h4 className="text-base sm:text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Lesson Overview</h4>
                          </div>
                          <p className="text-gray-700 leading-relaxed text-base">{lesson.overview || lesson.summaryTxt || 'No overview available'}</p>
                        </div>

                        {/* Practice Reminders */}
                        {lesson.practiceReminders && (
                          <div className="bg-white rounded-xl p-6 shadow-lg border border-green-100">
                            <div className="flex items-center mb-4">
                              <div className="text-2xl mr-3">💡</div>
                              <h4 className="text-base sm:text-lg font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Practice Reminders</h4>
                            </div>
                            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <p className="text-sm text-green-800">
                                💡 <strong>Tip:</strong> Use the dropdown menus and buttons below each reminder to add them to your Practice Tasks or create new Practice Goals. This helps you organize your practice sessions!
                              </p>
                            </div>
                            {Array.isArray(lesson.practiceReminders) ? (
                              <div className="space-y-3">
                                {lesson.practiceReminders.map((item, index) => {
                                  if (item.type === 'section') {
                                    return (
                                      <div key={index} className="mt-4 mb-2">
                                        <h5 className="text-sm font-semibold text-indigo-700 bg-indigo-50 px-3 py-2 rounded-md">
                                          {item.content}
                                        </h5>
                                      </div>
                                    );
                                  } else if (item.type === 'subsection') {
                                    return (
                                      <div key={index} className="ml-4">
                                        <h6 className="text-sm font-medium text-indigo-600">
                                          {item.content}
                                        </h6>
                                      </div>
                                    );
                                  } else if (item.type === 'bullet') {
                                    const isTransferred = transferredReminders.has(item.content);
                                    return (
                                      <div key={index} className="ml-8 group">
                                        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50/50 hover:bg-gray-100/50 transition-colors">
                                          <div className="flex items-start">
                                            <span className="text-indigo-600 mr-2">•</span>
                                            <span className={`flex-1 text-left ${isTransferred ? 'text-green-600 line-through' : 'text-gray-700'}`}>
                                              {item.content}
                                            </span>
                                            {isTransferred && (
                                              <div className="ml-4 flex items-center">
                                                <span className="text-green-600 text-sm font-medium">✓ Added</span>
                                              </div>
                                            )}
                                          </div>
                                          {!isTransferred && (
                                            <div className="ml-6 mt-2 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                                            {customTasks.length > 0 && (
                                              <select
                                                onChange={(e) => {
                                                  if (e.target.value) {
                                                    transferReminderToTask(item.content, e.target.value);
                                                    e.target.value = ''; // Reset selection
                                                  }
                                                }}
                                                className="text-xs border border-gray-300 rounded px-2 py-1 bg-white w-32 sm:w-auto min-w-0"
                                                disabled={transferringReminder === item.content}
                                              >
                                                <option value="">
                                                  {transferringReminder === item.content ? 'Adding...' : 'Add to task...'}
                                                </option>
                                                {customTasks.map((task) => (
                                                  <option key={task.id} value={task.id}>
                                                    {task.title} ({task.category})
                                                  </option>
                                                ))}
                                              </select>
                                            )}
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-1 sm:space-y-0 sm:space-x-1">
                                              <select
                                                value={newTaskCategory}
                                                onChange={(e) => setNewTaskCategory(e.target.value as any)}
                                                className="text-xs border border-gray-300 rounded px-1 py-1 bg-white w-24 sm:w-auto"
                                                disabled={creatingTaskFromReminder === item.content}
                                              >
                                                <option value="Scales">Scales</option>
                                                <option value="Exercises">Exercises</option>
                                                <option value="Studies">Studies</option>
                                                <option value="Repertoire">Repertoire</option>
                                                <option value="Ensemble">Ensemble</option>
                                              </select>
                                              <button
                                                onClick={() => createTaskFromReminder(item.content)}
                                                disabled={creatingTaskFromReminder === item.content}
                                                className="text-xs bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-2 py-1 rounded whitespace-nowrap"
                                                title="Create new task from this reminder"
                                              >
                                                {creatingTaskFromReminder === item.content ? 'Creating...' : 'Create Task'}
                                              </button>
                                            </div>
                                          </div>
                                        )}
                                        </div>
                                      </div>
                                    );
                                  } else {
                                    return (
                                      <div key={index} className="text-gray-700">
                                        {item.content}
                                      </div>
                                    );
                                  }
                                })}
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {typeof lesson.practiceReminders === 'string' ? lesson.practiceReminders.split('\n').map((line, index) => {
                                  const trimmedLine = line.trim();
                                  if (!trimmedLine || !trimmedLine.startsWith('-')) return null;
                                  
                                  const reminderText = trimmedLine.substring(1).trim();
                                  const isTransferred = transferredReminders.has(reminderText);
                                  return (
                                    <div key={index} className="group">
                                      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50/50 hover:bg-gray-100/50 transition-colors">
                                        <div className="flex items-start">
                                          <span className="text-indigo-600 mr-2">•</span>
                                          <span className={`flex-1 text-left ${isTransferred ? 'text-green-600 line-through' : 'text-gray-700'}`}>
                                            {reminderText}
                                          </span>
                                          {isTransferred && (
                                            <div className="ml-4 flex items-center">
                                              <span className="text-green-600 text-sm font-medium">✓ Added</span>
                                            </div>
                                          )}
                                        </div>
                                        {!isTransferred && (
                                          <div className="ml-6 mt-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                                          {customTasks.length > 0 && (
                                            <select
                                              onChange={(e) => {
                                                if (e.target.value) {
                                                  transferReminderToTask(reminderText, e.target.value);
                                                  e.target.value = ''; // Reset selection
                                                }
                                              }}
                                              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white w-32 sm:w-auto min-w-0"
                                              disabled={transferringReminder === reminderText}
                                            >
                                              <option value="">
                                                {transferringReminder === reminderText ? 'Adding...' : 'Add to task...'}
                                              </option>
                                              {customTasks.map((task) => (
                                                <option key={task.id} value={task.id}>
                                                  {task.title} ({task.category})
                                                </option>
                                              ))}
                                            </select>
                                          )}
                                          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-1 sm:space-y-0 sm:space-x-1">
                                            <select
                                              value={newTaskCategory}
                                              onChange={(e) => setNewTaskCategory(e.target.value as any)}
                                              className="text-xs border border-gray-300 rounded px-1 py-1 bg-white w-24 sm:w-auto"
                                              disabled={creatingTaskFromReminder === reminderText}
                                            >
                                              <option value="Scales">Scales</option>
                                              <option value="Exercises">Exercises</option>
                                              <option value="Studies">Studies</option>
                                              <option value="Repertoire">Repertoire</option>
                                              <option value="Ensemble">Ensemble</option>
                                            </select>
                                            <button
                                              onClick={() => createTaskFromReminder(reminderText)}
                                              disabled={creatingTaskFromReminder === reminderText}
                                              className="text-xs bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-2 py-1 rounded whitespace-nowrap"
                                              title="Create new task from this reminder"
                                            >
                                              {creatingTaskFromReminder === reminderText ? 'Creating...' : 'Create Task'}
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                      </div>
                                    </div>
                                  );
                                }) : (
                                  <p className="text-gray-500 italic">No practice reminders available</p>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Key Mindset Messages */}
                        {lesson.mindsetMessages && (
                          <div className="bg-white rounded-xl p-6 shadow-lg border border-yellow-100">
                            <div className="flex items-center mb-4">
                              <div className="text-2xl mr-3">🌟</div>
                              <h4 className="text-base sm:text-lg font-bold bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent">Key Mindset Messages</h4>
                            </div>
                            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4">
                              <div className="text-gray-800 whitespace-pre-wrap text-base leading-relaxed">
                                {lesson.mindsetMessages}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Key Quotes */}
                        {lesson.keyQuotes && (
                          <div className="bg-white rounded-xl p-6 shadow-lg border border-pink-100">
                            <div className="flex items-center mb-4">
                              <div className="text-2xl mr-3">💬</div>
                              <h4 className="text-base sm:text-lg font-bold bg-gradient-to-r from-pink-600 to-rose-600 bg-clip-text text-transparent">Key Quotes</h4>
                            </div>
                            <div className="space-y-4">
                              {Array.isArray(lesson.keyQuotes) ? (
                                lesson.keyQuotes.map((quote, index) => (
                                <div key={index} className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg p-4 border-l-4 border-pink-400">
                                  <blockquote className="text-gray-800 italic text-base leading-relaxed">
                                    "{quote}"
                                  </blockquote>
                                </div>
                              ))
                              ) : (
                                // Handle string format (legacy)
                                <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg p-4">
                                  <div className="text-gray-800 whitespace-pre-wrap text-base leading-relaxed">
                                    {typeof lesson.keyQuotes === 'string' ? lesson.keyQuotes : 'No key quotes available'}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Delete Lesson</h3>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-500">
                Are you sure you want to delete this lesson? This action cannot be undone.
                All lesson data including summary, tasks, and quiz will be permanently removed.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteLesson(showDeleteConfirm)}
                disabled={deletingLesson === showDeleteConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors disabled:opacity-50"
              >
                {deletingLesson === showDeleteConfirm ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonSummaries;
